import { useEffect } from 'react';

import { getDomains } from '@/helpers/getDomains';
import { logger } from '@/utils/logger/logger';
import {
  cancelTurn,
  waitForAttachSignalOrTimeout,
  type ChildRunDiscovery,
} from '@heidi/agents/data';
import {
  listenFileExecutorStatus,
  rpaBridge,
  type EnsureFileExecutorResult,
} from '@heidi/plugin-rpa';
import { sanitizeError } from '@heidi/runtime/logger';

import { getAuthToken } from './auth-token';

interface AttachState {
  parentRunId: string;
  cancelling: boolean;
  generation: number;
}

const attachStates = new Map<string, AttachState>();
/**
 * The native executor is a SINGLE slot shared across
 * parents, so every file child attaches one at a time regardless of which
 * parent discovered it: the chain serializes the `ensure_file_executor`
 * IPC calls instead of stampeding the slot with concurrent ensures (each
 * busy child would also spawn its own retry wait + status listener).
 */
let ensureTail: Promise<void> = Promise.resolve();
let attachGeneration = 0;

export const FILE_ATTACH_AUTH_POLL_MS = 100;

export const resetProjectFileExecutorAttachForTests = (): void => {
  attachStates.clear();
  ensureTail = Promise.resolve();
  attachGeneration = 0;
};

const stopFileExecutorForChild = async (
  childRunId: string,
  parentRunId: string
): Promise<void> => {
  try {
    await rpaBridge.stopFileExecutorFor(childRunId);
  } catch (error) {
    logger.warn('[project-file-executor-attach] Failed to stop file executor', {
      parent_agent_run_id: parentRunId,
      child_agent_run_id: childRunId,
      error: sanitizeError(error),
    });
  }
};

const isCurrentAttach = (discovery: ChildRunDiscovery, generation: number): boolean => {
  const current = attachStates.get(discovery.childRunId);
  return (
    current?.parentRunId === discovery.parentRunId && current.generation === generation
  );
};

const waitForAuthTokenPoke = (): Promise<void> =>
  waitForAttachSignalOrTimeout((done) => {
    const interval = window.setInterval(() => {
      if (getAuthToken()) done();
    }, FILE_ATTACH_AUTH_POLL_MS);
    return () => window.clearInterval(interval);
  });

const waitForFileExecutorSlotPoke = (): Promise<void> =>
  waitForAttachSignalOrTimeout(async (done) => {
    const unlisten = await listenFileExecutorStatus((event) => {
      if (event.payload.status?.state === 'stopped') {
        done();
      }
    });
    return unlisten;
  });

async function getAuthTokenForAttachOrCancel(
  discovery: ChildRunDiscovery,
  generation: number
): Promise<string | undefined> {
  const currentToken = getAuthToken();
  if (currentToken) return currentToken;

  await waitForAuthTokenPoke();
  if (!isCurrentAttach(discovery, generation)) return undefined;

  const bearerToken = getAuthToken();
  if (bearerToken) return bearerToken;

  logger.warn('[project-file-executor-attach] Missing auth token for child attach', {
    parent_agent_run_id: discovery.parentRunId,
    child_agent_run_id: discovery.childRunId,
  });
  await cancelChildRunOnce(discovery, 'missing-auth-token', generation);
  return undefined;
}

async function cancelChildRunOnce(
  discovery: ChildRunDiscovery,
  reason: string,
  generation: number
): Promise<void> {
  const current = attachStates.get(discovery.childRunId);
  if (current?.cancelling || !isCurrentAttach(discovery, generation)) {
    return;
  }

  attachStates.set(discovery.childRunId, {
    parentRunId: discovery.parentRunId,
    cancelling: true,
    generation,
  });

  if (rpaBridge.isAvailable()) {
    await stopFileExecutorForChild(discovery.childRunId, discovery.parentRunId);
  }

  try {
    await cancelTurn(discovery.childRunId);
  } catch (error) {
    logger.warn('[project-file-executor-attach] Failed to cancel child run', {
      parent_agent_run_id: discovery.parentRunId,
      child_agent_run_id: discovery.childRunId,
      reason,
      error: sanitizeError(error),
    });
  }
}

async function ensureFileExecutorForChild(
  discovery: ChildRunDiscovery,
  generation: number
): Promise<void> {
  const applyEnsureResult = (
    result: EnsureFileExecutorResult | undefined
  ): 'accepted' | 'busy' | 'stale' => {
    if (!result) return 'stale';
    if (!isCurrentAttach(discovery, generation)) {
      releaseStaleSlot(discovery, result);
      return 'stale';
    }
    return ownsSlot(discovery, result) ? 'accepted' : 'busy';
  };

  const ensure = async (): Promise<EnsureFileExecutorResult | undefined> => {
    const bearerToken = await getAuthTokenForAttachOrCancel(discovery, generation);
    if (!bearerToken || !isCurrentAttach(discovery, generation)) return undefined;

    // No uploadContext (the file executor uploads nothing) and no
    // presentation (headless — no panel, no focus return).
    return rpaBridge.ensureFileExecutor({
      agentRunId: discovery.childRunId,
      baseUrl: getDomains().baseV2Url,
      bearerToken,
    });
  };

  try {
    let result = applyEnsureResult(await ensure());
    if (result !== 'busy') return;

    await waitForFileExecutorSlotPoke();
    if (!isCurrentAttach(discovery, generation)) return;

    result = applyEnsureResult(await ensure());
    if (result !== 'busy') return;

    logger.warn('[project-file-executor-attach] File executor remained busy', {
      parent_agent_run_id: discovery.parentRunId,
      child_agent_run_id: discovery.childRunId,
    });
    await cancelChildRunOnce(discovery, 'executor-busy', generation);
  } catch (error) {
    logger.warn('[project-file-executor-attach] Failed to ensure file executor', {
      parent_agent_run_id: discovery.parentRunId,
      child_agent_run_id: discovery.childRunId,
      error: sanitizeError(error),
    });
    await cancelChildRunOnce(discovery, 'ensure-failed', generation);
  }
}

const ownsSlot = (
  discovery: ChildRunDiscovery,
  result: EnsureFileExecutorResult
): boolean =>
  result.outcome === 'accepted' && result.state.agentRunId === discovery.childRunId;

const releaseStaleSlot = (
  discovery: ChildRunDiscovery,
  result: EnsureFileExecutorResult
): void => {
  if (ownsSlot(discovery, result) && !attachStates.has(discovery.childRunId)) {
    void stopFileExecutorForChild(discovery.childRunId, discovery.parentRunId);
  }
};

async function attachExistingFileExecutorForRestoredChild(
  discovery: ChildRunDiscovery,
  generation: number
): Promise<void> {
  const bearerToken = await getAuthTokenForAttachOrCancel(discovery, generation);
  if (!bearerToken || !isCurrentAttach(discovery, generation)) return;

  try {
    const result = await rpaBridge.attachExistingFileExecutor({
      agentRunId: discovery.childRunId,
      bearerToken,
    });
    if (!isCurrentAttach(discovery, generation)) {
      if (result) releaseStaleSlot(discovery, result);
      return;
    }
    if (result && ownsSlot(discovery, result)) return;

    logger.warn(
      '[project-file-executor-attach] Restored child has no live file executor — cancelling',
      {
        parent_agent_run_id: discovery.parentRunId,
        child_agent_run_id: discovery.childRunId,
        current: result?.state.agentRunId,
      }
    );
  } catch (error) {
    logger.warn(
      '[project-file-executor-attach] Failed to attach the file executor for a restored child — cancelling',
      {
        parent_agent_run_id: discovery.parentRunId,
        child_agent_run_id: discovery.childRunId,
        error: sanitizeError(error),
      }
    );
  }

  await cancelChildRunOnce(discovery, 'restored-executor-missing', generation);
}

/**
 * Neither attach path rejects (a failure cancels the child internally), so
 * one child's failure can't wedge the chain.
 */
const enqueueFileExecutorEnsure = (
  discovery: ChildRunDiscovery,
  generation: number
): void => {
  ensureTail = ensureTail.then(() =>
    discovery.restored
      ? attachExistingFileExecutorForRestoredChild(discovery, generation)
      : ensureFileExecutorForChild(discovery, generation)
  );
};

interface UseProjectFileExecutorAttachArgs {
  /** False in desktop-target mode: native admission attaches the slot instead. */
  enabled?: boolean;
  parentRunId?: string;
  parentRunTerminal?: boolean;
  discoveries: ChildRunDiscovery[];
}

/**
 * A busy native slot is retried once before the child is cancelled.
 *
 * Lives in `rpa/agent-client` (not `projects/hooks` beside its recording
 * sibling): the `projects` root is locked against new modules, and
 * `packages/agents/data` is headless — this hook drives the Tauri bridge,
 * which a package door may not import.
 */
export const useProjectFileExecutorAttach = ({
  enabled = true,
  parentRunId,
  parentRunTerminal = false,
  discoveries,
}: UseProjectFileExecutorAttachArgs): void => {
  useEffect(() => {
    if (!enabled || !parentRunId) return;

    // A terminal parent tears down exactly like all its discoveries
    // vanishing — one prune path, no special case.
    const effectiveDiscoveries = parentRunTerminal ? [] : discoveries;
    const currentChildRunIds = new Set(
      effectiveDiscoveries.map((item) => item.childRunId)
    );
    for (const [childRunId, state] of attachStates) {
      if (state.parentRunId === parentRunId && !currentChildRunIds.has(childRunId)) {
        attachStates.delete(childRunId);
        if (rpaBridge.isAvailable()) {
          void stopFileExecutorForChild(childRunId, parentRunId);
        }
      }
    }

    if (!rpaBridge.isAvailable()) {
      return;
    }

    for (const discovery of effectiveDiscoveries) {
      const current = attachStates.get(discovery.childRunId);
      if (current && current.parentRunId === discovery.parentRunId) {
        continue;
      }

      const generation = ++attachGeneration;
      attachStates.set(discovery.childRunId, {
        parentRunId: discovery.parentRunId,
        cancelling: false,
        generation,
      });
      enqueueFileExecutorEnsure(discovery, generation);
    }
  }, [discoveries, enabled, parentRunId, parentRunTerminal]);
};
